import { formatNumber, JsonPipe, LowerCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';
import { RouterLink } from '@angular/router';
import {
  InvestmentStrategyModel,
  PortfolioCompositionModel,
  PortfolioModel,
  RiskLevelModel,
} from '@bp-core/src/lib/proto/common-message.pb';
import { fuseAnimations } from '@fuse/animations';
import { GoalStateEnum } from 'bp/modules/goal/+xstate/goal-new/goal-new-machine.schema';
import { GoalDataService } from 'bp/modules/goal/services/goal-data.service';
import { ColorService } from 'bp/shared/services/color.service';
import { UtilityService } from 'bp/shared/services/utility-service.service';
import { take } from 'rxjs';
import { BpCoreModule } from '../../../../../../bp-core/src/lib/bp-core.module';
import { ResponsiveButtonLargeDirective } from '../../directives/responsive-button-large.directive';
import { BlogFaqsComponent } from '../blog-faqs/blog-faqs.component';
import { BondsStocksGraphComponent } from '../bonds-stocks-graph/bonds-stocks-graph.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-risk-level-selector',
  templateUrl: './risk-level-selector.component.html',
  styleUrls: ['./risk-level-selector.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    BpCoreModule,
    FormsModule,
    ReactiveFormsModule,
    MatSliderModule,
    RouterLink,
    BondsStocksGraphComponent,
    MatButtonModule,
    ResponsiveButtonLargeDirective,
    BlogFaqsComponent,
    LowerCasePipe,
    MatIconModule,
    JsonPipe,
  ],
})
export class RiskLevelSelectorComponent implements OnInit {
  StateEnum = GoalStateEnum;
  title = 'Elige el nivel de riesgo';
  subtitle: string | undefined;
  form: UntypedFormGroup;
  currentRiskLevelId: number;

  currentRiskLevel: RiskLevelModel | undefined;
  currentPortfolio: PortfolioModel;
  compositions: any[];
  @Input() riskLevel: RiskLevelModel;
  @Input() riskLevelRecommended: RiskLevelModel;
  @Input() showFaqs = true;

  @Input() investmentStrategySelected: InvestmentStrategyModel;
  @Input() riskLevels: RiskLevelModel[];

  @Input() portfoliosFilteredForFinancialEntity: PortfolioModel[];
  portfoliosFilteredWithFunds: PortfolioModel[] | undefined;

  @Output() next = new EventEmitter<RiskLevelModel>();

  isLoading = false;

  get riskLevelControl() {
    return this.form.get('riskLevel');
  }

  constructor(
    private fb: FormBuilder,
    private colorService: ColorService,
    private utilityService: UtilityService,
    private goalDataService: GoalDataService,
  ) {}

  ngOnInit(): void {
    this.utilityService.scrollToTopSmoothly();

    if (!this.riskLevelRecommended && !this.riskLevel) {
      this.riskLevel = this.riskLevels[0];
    }

    // Ordenar los niveles de riesgo
    this.riskLevels = this.riskLevels.sort((a, b) => a.position - b.position);

    if (!this.riskLevel) this.riskLevel = this.riskLevelRecommended;

    let initialSliderPosition =
      this.riskLevels.findIndex(level => level.id === this.riskLevel.id) + 1;
    if (initialSliderPosition === 0) {
      initialSliderPosition =
        this.riskLevels.findIndex(level => level.title === this.riskLevel.title) + 1;
    }
    if (initialSliderPosition === 0) initialSliderPosition = 1;

    this.currentRiskLevel = this.riskLevel;
    this.isLoading = true;
    this.goalDataService
      .getPortfoliosByIds(
        this.portfoliosFilteredForFinancialEntity
          .filter(
            p =>
              p.investmentStrategyId == this.investmentStrategySelected?.id ||
              this.investmentStrategySelected?.id == null,
          )
          .map(p => p.id),
      )
      .pipe(take(1))
      .subscribe({
        next: portfolios => {
          this.portfoliosFilteredWithFunds = portfolios.values!;

          this.currentPortfolio = this.getPortfolio(this.currentRiskLevel!.id)!;
          this.isLoading = false;
        },
        error: err => {
          console.error(err);
          this.isLoading = false;
        },
      });

    this.form = this.fb.group({
      riskLevel: [initialSliderPosition],
    });
    this.riskLevelControl?.valueChanges.subscribe(sliderPosition => {
      this.currentRiskLevel = this.getRiskLevel(this.riskLevels[sliderPosition - 1].id)!;
      this.currentPortfolio = this.getPortfolio(this.currentRiskLevel.id)!;
    });
  }

  getRiskLevel(riskLevelId: number) {
    return this.riskLevels.find(level => level.id === riskLevelId);
  }
  getCurrentCompositionLabel() {
    if (!this.compositions) return '';
    this.compositions.sort((a, b) => a.displayOrder - b.displayOrder);
    let label = '';
    this.compositions.forEach((composition, index) => {
      if (index !== 0) {
        label += ', ';
      }
      label += `${formatNumber(composition.percentage, 'es-CL')}% ${composition.subCategoryName}`;
    });
    return label;
  }

  /**
   * Increases the current risk level by one.
   * If the current risk level is not the highest level, it updates the risk level control value to the next level.
   */
  increaseLevel() {
    const currentIndex = this.riskLevels.findIndex(level => level.id === this.currentRiskLevel?.id);
    if (currentIndex < this.riskLevels.length - 1) {
      this.riskLevelControl?.setValue(currentIndex + 2);
    }
  }
  /**
   * Decreases the current risk level.
   */
  decreaseLevel() {
    const currentIndex = this.riskLevels.findIndex(level => level.id === this.currentRiskLevel?.id);
    if (currentIndex > 0) {
      this.riskLevelControl?.setValue(currentIndex);
    }
  }
  getPortfolio(riskLevelId: number) {
    const compositions: PortfolioCompositionModel[] = [];

    const compositionsGraph: PortfolioCompositionModel[] = [];
    let portfolio: PortfolioModel | undefined;
    if (this.investmentStrategySelected) {
      portfolio = this.portfoliosFilteredWithFunds?.find(
        portfolio =>
          portfolio.riskLevelId === riskLevelId &&
          portfolio.investmentStrategyId === this.investmentStrategySelected?.id,
      );
    } else {
      portfolio = this.portfoliosFilteredWithFunds?.find(
        portfolio => portfolio.riskLevelId === riskLevelId,
      );
    }

    portfolio?.portfolioFunding?.forEach(fund => {
      if (fund?.funding?.fundingCompositions) {
        fund.funding.fundingCompositions.forEach(composition => {
          if (composition.subcategory) {
            // OBTIENE LA TAJADA QUE LE TOCA AL COMPOSITION
            // DEL FUND.PERCENTAGE
            const compositionPercent = Math.round((fund.percentage * composition.percentage) / 100);

            // CHECK IF THE COMPOSITION IS ALREADY CONSIDERATE ON THE compositions
            const alreadyAddedInCompositions = compositions?.find(
              compo => compo.subCategoryName == composition.subcategory?.name,
            );

            // CHECK IF THE COMPOSITION IS ALREADY CONSIDERATE ON THE compositionsGraph
            const alreadyAddedInCompositionsGraph = compositionsGraph?.find(
              compo => compo.subCategoryName == composition.subcategory?.category?.groupDisplayUuid,
            );

            if (alreadyAddedInCompositions) {
              alreadyAddedInCompositions.percentage += compositionPercent;
            } else {
              const customComposition = new PortfolioCompositionModel({
                subCategoryName: composition.subcategory?.name,
                percentage: compositionPercent,
                fundingId: fund.fundingId,
                subCategoryId: composition.subcategoryId,
                subcategory: composition.subcategory,
              });

              compositions?.push(customComposition);
            }

            if (alreadyAddedInCompositionsGraph) {
              alreadyAddedInCompositionsGraph.percentage += compositionPercent;
            } else {
              if (composition.subcategory?.category?.isDisplayedInGroupFunding) {
                const customCompositionGraph = new PortfolioCompositionModel({
                  subCategoryName: composition.subcategory?.category?.groupDisplayUuid,
                  percentage: compositionPercent,
                  fundingId: fund.fundingId,
                  subCategoryId: composition.subcategoryId,
                  subcategory: composition.subcategory,
                  color:
                    composition.subcategory?.category?.groupDisplayUuid === 'acciones'
                      ? this.colorService.getGreen(0)
                      : this.colorService.getBlue(0),
                });
                compositionsGraph?.push(customCompositionGraph);
              }
            }
          }
        });
      }
    });

    // THIS IS FOR NOT CONSIDERATE THE caja % ON THE compositionsGraph
    let percentageToConsiderate = 0;
    compositionsGraph.forEach(category => {
      percentageToConsiderate += category.percentage;
    });
    compositionsGraph.forEach(category => {
      if (percentageToConsiderate > 0) {
        category.percentage = Math.round((category.percentage * 100) / percentageToConsiderate);
      }
    });

    this.compositions = compositionsGraph;
    return portfolio;
  }
}
